import { Box, Button, IconButton, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Props, StepsRef } from './interface'
import { Step1 } from './step1'
import { useStyles } from './styles'
import { icons } from '../../../../../../utils/icons'
import StepperCreate from '../../../../../../components/stepperCreate'
import { MatchItems } from './mathsItems'
import { Ref, createRef, forwardRef, useImperativeHandle, useMemo, useState } from 'react'
import { MatchItemsRef } from './mathsItems/types'
import { ObjectGeneric } from '../../../../../../utils/types/generals'

const AgentSteps = forwardRef((props: Props, ref: Ref<StepsRef>) => {
  const styles = useStyles()
  const refStep = createRef<MatchItemsRef>()
  const { t } = useTranslation()
  const [dataLastStep, setDataLastStep] = useState<ObjectGeneric>({})
  const [createdList, setCreatedList] = useState<string[]>([])
  const lastStep = [2, 3].includes(props.steps ?? 0) || ((!!props.id || props.isParner) && (props.steps ?? 0) > 0)

  const rows = useMemo(() => {
    const list: ObjectGeneric[] = props.step3?.listMatched ?? dataLastStep?.rows
    return list?.filter(row => !createdList.some(id => row?.id == id)).map(row => {
      const data = dataLastStep?.dataList?.find((list: { id: string }) => list.id == row.id)
      return ({
        ...row,
        from: data?.from ?? null,
        to: data?.to ?? null,
        checkCalendar: !!data
      })
    })
  }, [createdList, dataLastStep?.rows, props.step3?.listMatched])

  const disabledList = useMemo(() => {
    const disableList = rows.filter((row: ObjectGeneric) => !dataLastStep.dataList?.some((list: { id: string }) => list.id == row.id))?.map((row: ObjectGeneric) => row.id)
    return disableList
  }, [rows])
  const activeLastStep = useMemo(() => {
    const disableList = rows.filter((row: ObjectGeneric) => !!dataLastStep.defaultChecks?.some((check: string) => check == row.id))?.length
    return !!disableList
  }, [rows, dataLastStep.defaultChecks])
  useImperativeHandle(ref, () => ({
    setData: (payload) => {
      const set = refStep.current?.setData
      if (set) {
        const idsSuccess: string[] = dataLastStep.defaultChecks.filter((check: string) => !payload.errors?.[check]?.length)
        setCreatedList(prev => [...prev, ...idsSuccess])
        setDataLastStep(prev => ({
          ...prev,
          itemWithErrors: payload.errors,
          dataList: prev.dataList?.filter((list: { id: string }) => !idsSuccess.some(success => success == list.id)),
          defaultChecks: prev.defaultChecks?.filter((id: string) => !idsSuccess.some(success => success == id))
        }))
        set({ ...payload, success: idsSuccess })
      }
    },
    defaulDataList: ({ data }) => {
      setDataLastStep(prev => ({
        ...prev,
        dataList: data
      }))
    }
  }))
  const CurrentStep = () => {
    if (!props.id && !props.isParner) {
      switch (props.steps) {
        case 0:
          return <Step1 colors={props.color} {...props.step1} />
        case 1:
          return <Step1 colors={props.color} {...props.step2} />
        case 2:
        case 3:
          return (
            <MatchItems
              ref={refStep}
              items={props.items}
              activeStep={activeStep}
              dataDefault={{ ...dataLastStep, disabledList }}
              listOfItemsToMatch={rows ?? []}
            />
          )
        default:
          return null
      }
    }
    if (!props.id && props.isParner) {
      switch (props.steps) {
        case 0:
          return <Step1 colors={props.color} {...props.step2} />
        case 1:
        case 2:
        case 3:
          return (
            <MatchItems
              ref={refStep}
              items={props.items}
              activeStep={activeStep}
              dataDefault={{ ...dataLastStep, disabledList }}
              listOfItemsToMatch={rows ?? []}
            />
          )
        default:
          return null
      }
    } else {
      switch (props.steps) {
        case 0:
          return <Step1 colors={props.color} {...props.step2} />
        case 1:
        case 2:
        case 3:
          return <MatchItems
            ref={refStep}
            items={props.items}
            id={props.id}
            activeStep={activeStep}
            dataDefault={{ ...dataLastStep, disabledList }}
            listOfItemsToMatch={rows ?? []}
          />
        default:
          return null
      }
    }

  }

  const goBack = () => {
    props.goBack()
  }

  const dataOfTheLastStep = () => {
    const getData = refStep.current?.getData
    if (lastStep && getData) {
      setDataLastStep(getData())
    }
  }
  const stepBack = () => {
    props.handleSteps(-1)
    dataOfTheLastStep()
  }

  const stepForward = () => {
    props.handleSteps(1)
    dataOfTheLastStep()
    if (lastStep) {
      const getData = refStep.current?.getData
      if (getData) {
        const data = getData()
        const listToSend = []

        const validateDataList = data.dataList.filter((list: ObjectGeneric) => !!rows.some((row: ObjectGeneric) => row.id == list.id))
        for (const check of data.defaultChecks) {
          const findData = validateDataList?.find((list: { id: string }) => list.id == check)

          if (findData) {
            listToSend.push(findData)
          }
        }

        props.getMatchData({ dataList: listToSend, isSendingAll: listToSend.length == rows.length })
      }
    }
  }

  const activeStep = (value: string[]) => {
    setDataLastStep(prev => ({ ...prev, defaultChecks: value }))
  }

  const disabled = () => {
    if (lastStep) {
      return !activeLastStep
    }

    return props.disabled
  }

  return (
    <Box sx={styles.container}>
      <Box sx={styles.header}>
        <Box>
          <Box sx={styles.headerLeft}>
            <IconButton onClick={goBack}>{icons('arrow-left-fill')}</IconButton>
            <Typography>{!props.id ? t('schedule.assigned.backToList') : t('schedule.assigned.editPlanification')}</Typography>
          </Box>

          <Box>
            <Typography sx={styles.title}>{!props.id ? t('schedule.assigned.calendarCreate') : t('schedule.assigned.calendarEdit')}</Typography>
          </Box>
        </Box>

        <Box sx={styles.stepper}>
          <StepperCreate color={props.color} steps={props.stepsArray} activeStep={props.activeStep} />
        </Box>
      </Box>

      <Box sx={styles.buttonsContainer}>
        {!!props.steps ? <Button onClick={stepBack}>{t('general.return')}</Button> : <Box className="back" />}
        {((props.steps ?? 0) < 2 && !props.id  && !props.isParner) && <Typography className="message">{props.steps === 0 && !props.id ? t('schedule.assigned.doYouNeedSelectedAlLeastOneS') : t('schedule.assigned.doYouNeedSelectedAlLeastOneQ')}</Typography>}
        <Button disabled={disabled()} className="next" variant="contained" onClick={stepForward}>
          {lastStep ? t(`schedule.buttons.${props.id ? 'updateScheduled' : 'addScheduled'}`) : t('general.continue')}
        </Button>
      </Box>

      <Box sx={styles.body}>{CurrentStep()}</Box>

      <Box sx={styles.buttonsContainer}>
        {!!props.steps ? <Button onClick={stepBack}>{t('general.return')}</Button> : <Box className="back" />}
        {((props.steps ?? 0) < 2 && !props.id && !props.isParner) && <Typography className="message">{props.steps === 0 && !props.id ? t('schedule.assigned.doYouNeedSelectedAlLeastOneS') : t('schedule.assigned.doYouNeedSelectedAlLeastOneQ')}</Typography>}
        <Button disabled={disabled()} className="next" variant="contained" onClick={stepForward}>
          {lastStep ? t(`schedule.buttons.${props.id ? 'updateScheduled' : 'addScheduled'}`) : t('general.continue')}
        </Button>
      </Box>
    </Box>
  )
})

export { AgentSteps }